import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BookOpen, ArrowRight } from 'lucide-react';
import OptimizedImage from '../components/OptimizedImage'; // ✅ Adjust path if needed

const StateStreamSection = () => {
  const states = [
    {
      name: 'Rajasthan',
      slug: 'rajasthan',
      image: '/rajasthan1.webp',
      colleges: 0,
      topStreams: ['Engineering', 'Medical', 'Management']
    },
    {
      name: 'Maharashtra',
      slug: 'maharashtra',
      image: '/maharashtra 1.webp',
      colleges: 0,
      topStreams: ['Engineering', 'Medical', 'Arts']
    },
    {
      name: 'Karnataka',
      slug: 'karnataka',
      image: '/karnatka 1.webp',
      colleges: 0,
      topStreams: ['Engineering', 'Medical', 'Management']
    },
    {
      name: 'Tamil Nadu',
      slug: 'tamil-nadu',
      image: '/tamilnadu 2.webp',
      colleges: 0,
      topStreams: ['Engineering', 'Medical', 'Arts']
    },
    {
      name: 'Delhi',
      slug: 'delhi',
      image: '/delhi 1.jpeg',
      colleges: 0,
      topStreams: ['Engineering', 'Management', 'Law']
    },
    {
      name: 'West Bengal',
      slug: 'west-bengal',
      image: '/west bengal 1.webp',
      colleges: 0, // ✅ Fixed missing "colleges" property
      topStreams: ['Engineering', 'Arts', 'Medical']
    }
  ];

  const streams = [
    {
      name: 'Engineering',
      slug: 'engineering',
      icon: '⚙️',
      colleges: 0,
      description: 'Top engineering colleges across India',
      color: 'bg-primary'
    },
    {
      name: 'Medical',
      slug: 'medical',
      icon: '🏥',
      colleges: 0,
      description: 'Premier medical colleges and MBBS programs',
      color: 'bg-danger'
    },
    {
      name: 'Management',
      slug: 'management',
      icon: '💼',
      colleges: 0,
      description: 'Top MBA and management institutes',
      color: 'bg-info'
    },
    {
      name: 'Law',
      slug: 'law',
      icon: '⚖️',
      colleges: 0,
      description: 'Leading law colleges and legal studies',
      color: 'bg-success'
    },
    {
      name: 'Arts & Humanities',
      slug: 'arts',
      icon: '🎨',
      colleges: 0,
      description: 'Liberal arts and humanities programs',
      color: 'bg-warning'
    },
    {
      name: 'Commerce',
      slug: 'commerce',
      icon: '📊',
      colleges: 0,
      description: 'Commerce and business studies',
      color: 'bg-secondary'
    }
  ];

  return (
    <section className="py-3 py-md-5 bg-white">
      <div className="container">
        {/* State-wise Section */}
        <div className="mb-5">
          <div className="text-center mb-4 mb-md-5 px-2">
            <h2 className="h1 fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>Colleges by State</h2>
            <p className="lead text-muted" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Explore colleges in your preferred state</p>
          </div>

          <div className="row g-2 g-md-4 px-2 px-md-0 states-mobile-grid">
            {states.map((state) => (
              <div key={state.slug} className="col-6 col-md-6 col-lg-4">
                <Link to={`/colleges/state/${state.slug}`} className="card college-card h-100 text-decoration-none">
                  <div className="position-relative">
                    <OptimizedImage
                      src={state.image}
                      alt={`${state.name} colleges`}
                      title={`Best colleges in ${state.name}`}
                      className="card-img-top"
                      style={{ height: 'clamp(120px, 20vw, 200px)', objectFit: 'cover' }}
                      loading="lazy"
                      fallbackSrc="https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=400"
                    />
                    <div
                      className="position-absolute bottom-0 start-0 end-0 p-3 text-white"
                      style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}
                    >
                      <h5 className="card-title fw-bold mb-1" style={{ fontSize: 'clamp(0.8rem, 3vw, 1.25rem)' }}>{state.name}</h5>
                      <div className="d-flex align-items-center gap-1" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)' }}>
                        <MapPin size={14} />
                        <span>{state.colleges} Colleges</span>
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-2 p-md-4">
                    <div className="mb-3">
                      <h6 className="fw-semibold text-dark mb-1 mb-md-2" style={{ fontSize: 'clamp(0.7rem, 2.5vw, 1rem)' }}>Popular Streams:</h6>
                      <div className="d-flex flex-wrap gap-1">
                        {state.topStreams.map((stream) => (
                          <span key={stream} className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: 'clamp(0.5rem, 1.8vw, 0.75rem)' }}>
                            {stream}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted" style={{ fontSize: 'clamp(0.7rem, 2vw, 0.875rem)' }}>View all colleges</span>
                      <ArrowRight className="text-primary" size={16} />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Stream-wise Section */}
        <div>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h2 className="h1 fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>Colleges by Stream</h2>
            <p className="lead text-muted" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Find colleges based on your field of interest</p>
          </div>

          <div className="row g-2 g-md-4 px-2 px-md-0 states-mobile-grid">
            {streams.map((stream) => (
              <div key={stream.slug} className="col-6 col-md-6 col-lg-4">
                <Link to={`/colleges/stream/${stream.slug}`} className="card college-card h-100 text-decoration-none">
                  <div className="card-body p-2 p-md-4">
                    <div className={`feature-icon ${stream.color} bg-opacity-10 mb-2`} style={{ width: 'clamp(2.5rem, 6vw, 4rem)', height: 'clamp(2.5rem, 6vw, 4rem)' }}>
                      <span style={{ fontSize: '2rem' }}>{stream.icon}</span>
                    </div>
                    <h5 className="card-title fw-bold text-dark mb-1" style={{ fontSize: 'clamp(0.8rem, 3vw, 1.25rem)' }}>{stream.name}</h5>
                    <p className="card-text text-muted mb-2 mb-md-4" style={{ fontSize: 'clamp(0.6rem, 2vw, 0.875rem)' }}>{stream.description}</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                        <BookOpen size={14} />
                        <small>{stream.colleges} Colleges</small>
                      </div>
                      <ArrowRight className="text-primary" size={16} />
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-4 mt-md-5 px-2 px-md-0">
          <div className="bg-blue-gradient rounded-4 p-3 p-md-5 text-center text-white">
            <h3 className="h2 fw-bold mb-3" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Can't Find What You're Looking For?</h3>
            <p className="lead text-blue-100 mb-3 mb-md-4" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
              Use our advanced search to find colleges that match your specific requirements
            </p>
            <Link to="/college-reviews" className="btn btn-light btn-lg fw-semibold w-100 w-sm-auto" style={{ minHeight: '48px', maxWidth: '250px' }}>
              Advanced Search
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StateStreamSection;
